// client/src/components/servicos/Servicos.js - VERSÃO FINAL CORRIGIDA (SEM CABEÇALHO DUPLICADO) + SUPORTE A POOL
import React, { Fragment, useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useServicos, useMutateServicos } from '../../hooks/useServicos';
import { useAccountUsers, useMyAccountDetails } from '../../hooks/useAccounts';
import { useRecursos } from '../../hooks/useRecursos';
import { useRecursosPool } from '../../hooks/useRecursos';
import { useUnidadesParaFormulario } from '../../hooks/useUnidades';
import { useTodasCategorias } from '../../hooks/useCategorias';
import useAlert from '../../hooks/useAlert';
import ServicoFormWrapper from './ServicoFormWrapper';
import ServicoRow from './ServicoRow';
import ServicoImages from './ServicoImages';
import FiltroServicos from './FiltroServicos';
import { PlusCircle, Filter as FilterIcon, Loader2, ClipboardList, XCircle, Eye, Image as ImageIcon, Trash2 } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

// ✅ Função para validar ObjectId do MongoDB (24 caracteres hexadecimais)
const isValidObjectId = (id) => {
    if (!id || typeof id !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(id);
};

const Servicos = () => {
    const { setAlert } = useAlert();
    const location = useLocation();
    const navigate = useNavigate();

    const [viewState, setViewState] = useState({
        showList: true,
        showForm: false,
        showImageManager: false
    });
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentServico, setCurrentServico] = useState(null);
    const [formMode, setFormMode] = useState('create');
    const [selectedServico, setSelectedServico] = useState(null);
    const [prefillData, setPrefillData] = useState(null);
    const servicosPerPage = 10;

    // ✅ Prefill vindo do Catálogo de Modelos (ver ImportarCatalogoPage.js —
    // Conta clica "Importar", navega pra cá já com os dados do item).
    useEffect(() => {
        if (location.state?.prefillCatalogo) {
            setPrefillData(location.state.prefillCatalogo);
            setCurrentServico(null);
            setFormMode('create');
            setViewState({ showList: false, showForm: true, showImageManager: false });
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const { data: accountDetails, isLoading: isAccountDetailsLoading, isError: isAccountDetailsError } = useMyAccountDetails();
    const accountId = accountDetails?._id;
    const isAccountIdValid = isValidObjectId(accountId);

    const queryParams = useMemo(() => {
        if (!isAccountIdValid) return {};
        return { account: accountId, page: currentPage, limit: servicosPerPage, ...activeFilters };
    }, [accountId, currentPage, activeFilters, isAccountIdValid]);

    const servicosQuery = useServicos(queryParams, { enabled: isAccountIdValid });
    const servicosData = servicosQuery.data || null;
    const isLoading = servicosQuery.isLoading || false;
    const isFetching = servicosQuery.isFetching || false;
    const refetch = servicosQuery.refetch || (() => {});
    const isServicosError = servicosQuery.isError || false;

    const recursosQuery = useRecursos(isAccountIdValid ? accountId : null, { limit: 100, enabled: isAccountIdValid });
    const recursosData = recursosQuery.data || { data: [] };
    const isLoadingRecursos = recursosQuery.isLoading || false;

    // ✅ NOVO: Buscar pools de recursos
    const poolsQuery = useRecursosPool(isAccountIdValid ? accountId : null, { limit: 100, enabled: isAccountIdValid });
    const poolsData = poolsQuery.data || { data: [] };
    const isLoadingPools = poolsQuery.isLoading || false;

    const accountUsersQuery = useAccountUsers({ enabled: isAccountIdValid });
    const accountUsers = accountUsersQuery.data || [];
    const isLoadingAccountUsers = accountUsersQuery.isLoading || false;

    const unidadesQuery = useUnidadesParaFormulario(isAccountIdValid ? accountId : null, { enabled: isAccountIdValid });
    const unidadesData = unidadesQuery.data || { data: [] };
    const isLoadingUnidades = unidadesQuery.isLoading || false;

    const categoriasQuery = useTodasCategorias(isAccountIdValid ? accountId : null, { tipo: 'servico', includeInactive: false, enabled: isAccountIdValid });
    const categoriasData = categoriasQuery.data || { data: [] };
    const isLoadingCategorias = categoriasQuery.isLoading || false;

    const { deleteServico } = useMutateServicos();
    const isDeleting = deleteServico.isPending || false;

    const servicos = useMemo(() => {
        if (!servicosData) return [];
        if (servicosData.data && Array.isArray(servicosData.data)) return servicosData.data;
        if (Array.isArray(servicosData)) return servicosData;
        if (servicosData.servicos && Array.isArray(servicosData.servicos)) return servicosData.servicos;
        if (servicosData._id || servicosData.id) return [servicosData];
        return [];
    }, [servicosData]);

    const recursosArray = useMemo(() => {
        if (!recursosData) return [];
        if (recursosData.data && Array.isArray(recursosData.data)) return recursosData.data;
        if (Array.isArray(recursosData)) return recursosData;
        return [];
    }, [recursosData]);

    // ✅ NOVO: Array de pools
    const poolsArray = useMemo(() => {
        if (!poolsData) return [];
        if (poolsData.data && Array.isArray(poolsData.data)) return poolsData.data;
        if (Array.isArray(poolsData)) return poolsData;
        return [];
    }, [poolsData]);

    const unidadesArray = useMemo(() => {
        if (!unidadesData) return [];
        if (unidadesData.data && Array.isArray(unidadesData.data)) return unidadesData.data;
        if (Array.isArray(unidadesData)) return unidadesData;
        if (unidadesData.unidades && Array.isArray(unidadesData.unidades)) return unidadesData.unidades;
        return [];
    }, [unidadesData]);

    const categoriasArray = useMemo(() => {
        if (!categoriasData) return [];
        if (categoriasData.data && Array.isArray(categoriasData.data)) return categoriasData.data;
        if (Array.isArray(categoriasData)) return categoriasData;
        return [];
    }, [categoriasData]);

    const servicosValidos = useMemo(() => {
        if (servicos.length === 0) return [];
        return servicos.filter(servico => {
            if (!servico) return false;
            return (servico._id && servico._id !== '') || (servico.id && servico.id !== '');
        });
    }, [servicos]);

    const totalServicos = useMemo(() => {
        if (!servicosData) return 0;
        if (servicosData.totalCount !== undefined) return servicosData.totalCount;
        if (servicosData.total !== undefined) return servicosData.total;
        if (servicosData.count !== undefined) return servicosData.count;
        return servicos.length;
    }, [servicosData, servicos.length]);

    const pagination = servicosData?.pagination || {};

    const handleApplyFilters = useCallback((filters) => { 
        setCurrentPage(1); 
        setActiveFilters(filters); 
    }, []);

    const handleAdicionarClick = useCallback(() => { 
        setCurrentServico(null);
        setFormMode('create');
        setViewState({ showList: false, showForm: true, showImageManager: false });
        setShowFilters(false);
    }, []);

    const handleEditClick = useCallback((servico) => {
        const servicoId = servico._id || servico.id;
        if (!servico || !servicoId) {
            setAlert('Não foi possível editar o serviço: ID não encontrado', 'danger');
            return;
        }
        setCurrentServico(servico);
        setFormMode('edit');
        setViewState({ showList: false, showForm: true, showImageManager: false });
        setShowFilters(false);
        setSelectedServico(null);
    }, [setAlert]);

    const handleEditImagesClick = useCallback((servico) => {
        const servicoId = servico._id || servico.id;
        if (!servico || !servicoId) {
            setAlert('Não foi possível gerenciar imagens: ID do serviço não encontrado', 'danger');
            return;
        }
        setCurrentServico(servico);
        setViewState({ showList: false, showForm: false, showImageManager: true });
        setShowFilters(false);
        setSelectedServico(null);
    }, [setAlert]);

    const handleCloseForms = useCallback(() => {
        setViewState({ showList: true, showForm: false, showImageManager: false });
        setCurrentServico(null);
        setFormMode('create');
        setPrefillData(null);
    }, []);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForms();
        refetch();
        setAlert('Serviço salvo com sucesso!', 'success');
    }, [handleCloseForms, refetch, setAlert]);

    const handleDelete = useCallback((servicoId) => {
        if (!servicoId) {
            setAlert('ID do serviço não encontrado', 'danger');
            return;
        }
        if (window.confirm('Tem certeza que deseja remover este serviço?')) {
            deleteServico.mutate(servicoId, {
                onSuccess: () => {
                    setAlert('Serviço removido com sucesso!', 'success');
                    setSelectedServico(null);
                    refetch();
                },
                onError: (error) => {
                    setAlert(error.message || 'Erro ao remover serviço', 'danger');
                }
            });
        }
    }, [deleteServico, refetch, setAlert]);

    const handleImageUpdate = useCallback((updatedServico) => {
        setCurrentServico(updatedServico);
    }, []);

    const isDataLoading = isAccountDetailsLoading || isLoading || isLoadingRecursos || isLoadingPools || 
                           isLoadingAccountUsers || isLoadingUnidades || isLoadingCategorias;

    const hasErrors = isAccountDetailsError || isServicosError || recursosQuery.isError || 
                      poolsQuery.isError || accountUsersQuery.isError || unidadesQuery.isError || categoriasQuery.isError;

    const canShowEditForm = viewState.showForm && formMode === 'edit' && currentServico;
    const canShowCreateForm = viewState.showForm && formMode === 'create';
    const canShowImageManager = viewState.showImageManager && currentServico;

    if (isAccountDetailsLoading) {
        return (
            <div className="panel-state-wrapper">
                <div className="flex flex-col items-center justify-center p-8">
                    <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
                    <p className="text-gray-600">Carregando informações da conta...</p>
                </div>
            </div>
        );
    }

    if (!isAccountIdValid && !isAccountDetailsLoading) {
        return (
            <div className="panel-state-wrapper">
                <div className="alert alert-warning">
                    <h3>Aguardando identificação da conta</h3>
                    <p>Aguardando carregamento do ID da conta. Isso pode levar alguns segundos...</p>
                    <div className="mt-4">
                        <Loader2 size={20} className="animate-spin inline mr-2 text-blue-600" />
                        <span className="text-sm text-gray-600">Tentando obter ID da conta...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (hasErrors) {
        return (
            <div className="panel-state-wrapper">
                <div className="alert alert-danger">
                    <h3>Erro ao carregar dados</h3>
                    <p>Ocorreu um erro ao carregar os dados necessários. Por favor, tente novamente.</p>
                    <button onClick={() => window.location.reload()} className="action-button action-button-primary mt-2">
                        Recarregar Página
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<ClipboardList />}
                    title="Gerenciamento de Serviços"
                    actions={<>
                        {!viewState.showForm && !viewState.showImageManager && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isDataLoading}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {(viewState.showForm || viewState.showImageManager) && (
                            <button onClick={handleCloseForms} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {canShowEditForm && (
                    <div className="content-panel">
                        <ServicoFormWrapper 
                            servicoId={currentServico?._id || currentServico?.id}
                            onCloseForm={handleCloseForms}
                            onCreateSuccess={handleSaveSuccess}
                            accountId={accountId}
                            mode={formMode}
                        />
                    </div>
                )}

                {canShowCreateForm && (
                    <div className="content-panel">
                        <ServicoFormWrapper
                            servicoId={null}
                            onCloseForm={handleCloseForms}
                            onCreateSuccess={handleSaveSuccess}
                            accountId={accountId}
                            mode={formMode}
                            prefillData={prefillData}
                        />
                    </div>
                )}

                {canShowImageManager && (
                    <div className="content-panel">
                        <ServicoImages
                            key={`images-${currentServico._id || currentServico.id}`}
                            servico={currentServico}
                            isCreating={false}
                            onUpdate={handleImageUpdate}
                        />
                    </div>
                )}

                {showFilters && viewState.showList && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtros</h3>
                        <FiltroServicos 
                            onFilterSubmit={handleApplyFilters} 
                            loading={isLoading} 
                            accountId={accountId}
                            accountUsers={accountUsers}
                            recursos={recursosArray}
                            unidades={unidadesArray}
                            categorias={categoriasArray}
                        />
                    </div>
                )}
            </div>

            {viewState.showList && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading && servicosValidos.length === 0 ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <p className="mt-2">Carregando serviços...</p>
                            </div>
                        ) : servicosValidos.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Categoria</th>
                                        <th>Tipo</th>
                                        <th>Status</th>
                                        <th>Preço</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {servicosValidos.map(servico => (
                                        <ServicoRow
                                            key={servico._id || servico.id}
                                            servico={servico}
                                            onOpenActions={setSelectedServico}
                                            loading={isDeleting}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <div className="text-center py-8">
                                    <ClipboardList size={48} className="mx-auto text-gray-400 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Nenhum serviço encontrado
                                    </h3>
                                    <p className="text-gray-500 mb-4">
                                        {Object.keys(activeFilters).length > 0 
                                            ? 'Tente ajustar os filtros ou criar um novo serviço.'
                                            : 'Comece criando seu primeiro serviço.'
                                        }
                                    </p>
                                    <div className="flex gap-2 justify-center">
                                        {Object.keys(activeFilters).length > 0 && (
                                            <button 
                                                onClick={() => setActiveFilters({})}
                                                className="action-button action-button-secondary"
                                            >
                                                Limpar Filtros
                                            </button>
                                        )}
                                        <button
                                            onClick={handleAdicionarClick}
                                            className="action-button action-button-primary"
                                            disabled={!isAccountIdValid}
                                        >
                                            <PlusCircle size={18} className="icon" />
                                            Criar Primeiro Serviço
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <Pagination
                        totalItems={totalServicos}
                        itemsPerPage={servicosPerPage}
                        onPageChange={setCurrentPage}
                        currentPage={currentPage}
                    />
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Serviço"
                visible={viewState.showList && !viewState.showForm && !viewState.showImageManager}
                disabled={isDataLoading || !isAccountIdValid}
                icon={<ClipboardList size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedServico}
                onClose={() => setSelectedServico(null)}
                title={selectedServico?.nome || 'Serviço'}
                actions={selectedServico ? [
                    {
                        key: 'imagens',
                        label: 'Gerenciar Imagens',
                        icon: <ImageIcon size={20} className="icon" />,
                        onClick: () => handleEditImagesClick(selectedServico),
                    },
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedServico),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedServico._id || selectedServico.id),
                        destructive: true,
                        loading: isDeleting,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default Servicos;